import {
  ICanDropOutcome,
  IGridDropPosition,
  IGridPosition,
} from '../../../components/DndGrid';
import {
  getRowsWithMovedContent,
  getRowsWithUpdatedColSpan,
  getRowsWithUpdatedRowHeights,
} from './grid/gridUpdaters';
import {
  DropType,
  IColSpanUpdater,
  IRowMinHeightUpdater,
} from '../../../components/DndGrid/types';
import { INSERT_DISABLED_MESSAGE } from '../DashboardInstance/constants';
import { MouseEventHandler, useCallback, useState } from 'react';
import {
  IDashboardInstanceFieldsFragment,
  IExternalEmbedFieldsFragment,
  IImageEmbedFieldsFragment,
  IRichTextFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import useDashboardMediaPopoverController from '../DashboardInstance/components/DashboardMediaPopover/useDashboardMediaPopoverController';
import { DashboardMediaPopoverMode } from '../DashboardInstance/components/DashboardMediaPopover/types';
import useDashboardUpdateGrid from './grid/useDashboardUpdateGrid';
import useDashboardEditExternalEmbed from './edit/useDashboardEditExternalEmbed';
import useDashboardEditRichText from './edit/useDashboardEditRichText';
import useDashboardEditImageEmbed from './edit/useDashboardEditImageEmbed';
import { DASHBOARD_MAX_COLUMNS_PER_ROW } from './constants';

/**
 * Combines all edit operations on the dashboard. Uses individual useDashboardEdit* hooks to perform the edit operation
 *
 * @param dashboard - the current dashboard
 */
const useDashboardEditContent = (
  dashboard: IDashboardInstanceFieldsFragment,
) => {
  const [loading, setLoading] = useState(false);
  const { popoverProps, openPopover } = useDashboardMediaPopoverController();
  const { saveDashboardGrid } = useDashboardUpdateGrid(dashboard);

  const { rows } = dashboard;

  const handleDrop = useCallback(
    async (source: IGridPosition, destination: IGridDropPosition) => {
      setLoading(true);
      try {
        await saveDashboardGrid(
          getRowsWithMovedContent({
            source,
            destination,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [saveDashboardGrid],
  );

  const canDrop = useCallback(
    (
      source: IGridPosition,
      destination: IGridDropPosition,
    ): ICanDropOutcome => {
      const canDrop =
        destination.type === DropType.ROW || // always allow adding a new row...
        source.rowIndex === destination.rowIndex || // ... always allow rearranging on the same row ...
        // ... but check if destination has not exceeded the limit
        rows[destination.rowIndex].columns.length <
          DASHBOARD_MAX_COLUMNS_PER_ROW;

      return canDrop
        ? { canDrop }
        : { canDrop, reason: INSERT_DISABLED_MESSAGE };
    },
    [rows],
  );

  const updateColSpanHandler = useCallback<IColSpanUpdater>(
    async (rowIndex, newColSpan) => {
      setLoading(true);
      try {
        await saveDashboardGrid(
          getRowsWithUpdatedColSpan({
            rowIndex,
            newColSpan,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [saveDashboardGrid],
  );

  const updateRowMinHeightHandler = useCallback<IRowMinHeightUpdater>(
    async (rowIndex, newMinHeight) => {
      setLoading(true);
      try {
        await saveDashboardGrid(
          getRowsWithUpdatedRowHeights({
            rowIndex,
            newMinHeight,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [saveDashboardGrid],
  );

  const { editDashboardRichText } = useDashboardEditRichText(dashboard);
  const createEditRichTextHandler = useCallback(
    (richText: IRichTextFieldsFragment) => async (html: string) => {
      setLoading(true);
      try {
        await editDashboardRichText({ richTextId: richText.id, html });
      } finally {
        setLoading(false);
      }
    },
    [editDashboardRichText],
  );

  const { startImageEmbedEditUpload, imageEmbedUploadProps } =
    useDashboardEditImageEmbed(dashboard, setLoading);
  const createEditImageEmbedHandler = useCallback(
    (imageEmbed: IImageEmbedFieldsFragment) => () => {
      startImageEmbedEditUpload(imageEmbed);
    },
    [startImageEmbedEditUpload],
  );

  const { editDashboardExternalEmbed } =
    useDashboardEditExternalEmbed(dashboard);
  const createEditExternalEmbedHandler = useCallback(
    (externalEmbed: IExternalEmbedFieldsFragment): MouseEventHandler =>
      (ev) => {
        openPopover({
          mode: DashboardMediaPopoverMode.Edit,
          anchorPosition: { left: ev.clientX, top: ev.clientY },
          url: externalEmbed.url,
          onSubmit: async (url) => {
            setLoading(true);
            try {
              await editDashboardExternalEmbed({
                externalEmbedId: externalEmbed.id,
                url,
              });
            } finally {
              setLoading(false);
            }
          },
        });
      },
    [editDashboardExternalEmbed, openPopover],
  );

  return {
    loading,
    handleDrop,
    canDrop,
    updateColSpanHandler,
    updateRowMinHeightHandler,
    createEditRichTextHandler,
    createEditImageEmbedHandler,
    createEditExternalEmbedHandler,
    popoverProps,
    imageEmbedUploadProps,
  };
};

export default useDashboardEditContent;
